import React, { createContext, useCallback, useMemo, useReducer } from "react";
import useChatStream from "../api/useChatStream";
import { useChatAPI } from "../api/hooks/useChatAPI";
import type { StreamingAssistantMessage } from "../Components/ChatPanel/ChatThread/ChatMessage/messageTypes";
import {
  useStreamNotifications,
  STREAMING_TOAST_ID,
  STREAMING_TOAST_DELAY_MS,
  DONE_CHAT_TOAST_ID,
} from "./useStreamNotifications";
import useStreamEventHandlers, {
  type ChatStreamEvent,
} from "./useStreamEventHandlers";

// Re-exported for consumers that import these from ChatStreamProvider
export { STREAMING_TOAST_ID, STREAMING_TOAST_DELAY_MS, DONE_CHAT_TOAST_ID };
export type { ChatStreamEvent };

interface ChatStreamState {
  isStreaming: boolean;
  streamingChatId: string | null;
}

const initialChatStreamState: ChatStreamState = {
  isStreaming: false,
  streamingChatId: null,
};

export interface ChatStreamContextValue {
  streamState: ChatStreamState;
  startStream: (params: {
    chatId: string;
    message: string;
    pendingUserMessage: { content: string; message_uuid: string } | null;
    initialAssistantMessage: StreamingAssistantMessage;
  }) => void;
  stopStream: (params: {
    chatId: string;
    shouldAppendMessages?: boolean;
    shouldOnlyAppendUserMessage?: boolean;
  }) => void;
  registerEventHandler: (handler: (event: ChatStreamEvent) => void) => void;
  unregisterEventHandler: () => void;
  getAccumulatedMessage: () => StreamingAssistantMessage | null;
  getPendingUserMessage: () => { content: string; message_uuid: string } | null;
}

export const initialChatStreamContextValue: ChatStreamContextValue = {
  streamState: initialChatStreamState,
  startStream: () => {},
  stopStream: () => {},
  registerEventHandler: () => {},
  unregisterEventHandler: () => {},
  getAccumulatedMessage: () => null,
  getPendingUserMessage: () => null,
};

export const ChatStreamContext = createContext<ChatStreamContextValue>(
  initialChatStreamContextValue
);

const ChatStreamProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [streamState, updateStreamState] = useReducer<
    (
      state: ChatStreamState,
      newState: Partial<ChatStreamState>
    ) => ChatStreamState
  >((state, newState) => ({ ...state, ...newState }), {
    ...initialChatStreamState,
  });

  const { onChatRenamed, onStreamComplete } = useChatAPI();
  const {
    cancelStreamingToast,
    scheduleWorkingToastIfOnChatPage,
    showDoneToast,
  } = useStreamNotifications();

  const {
    handleMessageEvent,
    registerEventHandler,
    unregisterEventHandler,
    getAccumulatedMessage,
    getPendingUserMessage,
    resetForNewStream,
    clearStreamRefs,
    handleStreamComplete,
  } = useStreamEventHandlers({
    updateStreamState,
    cancelStreamingToast,
    scheduleWorkingToastIfOnChatPage,
    showDoneToast,
    onChatRenamed,
    onStreamComplete,
  });

  const { sendMessage: sendMessageToApi, stopStreaming: stopStreamingApi } =
    useChatStream({ handleMessageEvent });

  const startStream = useCallback(
    ({
      chatId,
      message,
      pendingUserMessage,
      initialAssistantMessage,
    }: {
      chatId: string;
      message: string;
      pendingUserMessage: { content: string; message_uuid: string } | null;
      initialAssistantMessage: StreamingAssistantMessage;
    }) => {
      resetForNewStream(pendingUserMessage, initialAssistantMessage);
      updateStreamState({ isStreaming: true, streamingChatId: chatId });
      sendMessageToApi(chatId, message).catch(() => {
        // Error events are already emitted by useChatStream's handleMessageEvent;
        // this catch prevents unhandled promise rejections from the initial fetch.
      });
    },
    [resetForNewStream, sendMessageToApi]
  );

  const stopStream = useCallback(
    ({
      chatId,
      shouldAppendMessages = true, // false when user manually aborts (don't persist partial response)
      shouldOnlyAppendUserMessage = false,
    }: {
      chatId: string;
      shouldAppendMessages?: boolean;
      shouldOnlyAppendUserMessage?: boolean;
    }) => {
      stopStreamingApi();
      if (shouldAppendMessages) {
        handleStreamComplete(chatId, shouldOnlyAppendUserMessage);
      }
      updateStreamState({ isStreaming: false, streamingChatId: null });
      clearStreamRefs();
      cancelStreamingToast();
    },
    [
      stopStreamingApi,
      handleStreamComplete,
      clearStreamRefs,
      cancelStreamingToast,
    ]
  );

  const contextValue = useMemo(
    () => ({
      streamState,
      startStream,
      stopStream,
      registerEventHandler,
      unregisterEventHandler,
      getAccumulatedMessage,
      getPendingUserMessage,
    }),
    [
      streamState,
      startStream,
      stopStream,
      registerEventHandler,
      unregisterEventHandler,
      getAccumulatedMessage,
      getPendingUserMessage,
    ]
  );

  return (
    <ChatStreamContext.Provider value={contextValue}>
      {children}
    </ChatStreamContext.Provider>
  );
};

export default ChatStreamProvider;
